import { useSearch } from "@tanstack/react-router";
import { HelpCircle } from "lucide-react";

import { InfoPopover } from "@/components/cinch/InfoPopover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useSummary, useWeeklyKpi, type AePacingRow } from "../api";
import { CroForecastFilterBar } from "../components/FilterBar";
import { LockedBanner } from "../components/LockedBanner";
import { CroForecastSubNav } from "../components/SubNav";
import { fmtCents, fmtCount, fmtDate, fmtMonthName, fmtPct } from "../format";
import { resolveSearch, type CroForecastSearch } from "../search";

/**
 * Weekly KPI Summary — modeled on Stan's spreadsheet screenshot. Three
 * blocks:
 *   1. Team Forecast / Closed (the headline)
 *   2. Pipeline coverage + open pipeline metrics
 *   3. CLTND / CLTC / Pushed / pipeline movement
 *   4. CRO net-revenue forecast (month + quarter)
 *
 * A few fields are flagged "Confirm definition" — rendered as small
 * orange chips next to the unknown row so Stan can clarify before
 * Phase 4.1 wires real data behind them.
 */
export function CroForecastWeeklyKpiPage() {
  const search = useSearch({ from: "/cro-forecast/weekly-kpi" }) as CroForecastSearch;
  const { year, week } = resolveSearch(search);
  const summary = useSummary(year, week);
  const kpi = useWeeklyKpi(year, week);

  const data = summary.data;
  const isLoading = summary.isLoading || kpi.isLoading;
  const error = summary.error ?? kpi.error;

  return (
    <div className="flex flex-col">
      <CroForecastFilterBar search={search} />
      <CroForecastSubNav search={search} />
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <LockedBanner
          status={kpi.data?.lock_status ?? summary.data?.lock_status}
          isoYear={year}
          isoWeek={week}
        />
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {error && (
          <p className="text-sm text-destructive">
            Failed: {(error as Error).message}
          </p>
        )}
        {data && (
          <>
            {/* Block 1 — Team Forecast / Closed */}
            <KpiBlock title="Team forecast">
              <KpiRow
                label={`Team ${fmtMonthName(data.fiscal_month)} forecast`}
                value={sumForecast(data.forecast_by_segment_month)}
              />
              <KpiRow
                label="Team closed"
                value={sumClosed(data.closed_by_segment_month)}
              />
            </KpiBlock>

            {/* Block 2 — Pipeline coverage */}
            <KpiBlock title={`${fmtMonthName(data.fiscal_month)} pipeline`}>
              <KpiRow
                label={`${fmtMonthName(data.fiscal_month)} open pipeline`}
                value={data.banner.total_amount_cents}
              />
              <KpiCountRow
                label="# Deals"
                value={data.banner.total_deal_count}
              />
              <KpiPctRow
                label="Forecast coverage"
                value={coveragePct(
                  data.banner.total_amount_cents,
                  sumForecast(data.forecast_by_segment_month),
                )}
                hint="Open pipeline / Team forecast. >100% means coverage exceeds the call."
              />
              <KpiRow
                label="Forecast FB"
                value={null}
                placeholder="Confirm definition — best-case forecast? Final-back? Need a definition pass."
              />
              <KpiPctRow
                label="Quota coverage"
                value={coveragePct(
                  data.banner.total_amount_cents,
                  // Plan/quota for the month — fallback to the plan_month_cents.
                  data.plan_month_cents,
                )}
                hint="Open pipeline / monthly plan. The spreadsheet's '4× quota' goal lands ~137% style."
              />
            </KpiBlock>

            {/* Block 3 — Total open / number / avg size */}
            <KpiBlock title="Open">
              <KpiRow
                label="Total open"
                value={data.banner.total_amount_cents}
              />
              <KpiCountRow
                label="Number of deals"
                value={data.banner.total_deal_count}
              />
              <KpiRow
                label="Average deal size"
                value={data.banner.avg_deal_cents}
              />
            </KpiBlock>

            {/* Block 4 — Pipeline movement aliases. The spreadsheet uses
                CLTND/CLTC shorthand which we keep as labels; values come
                from the weekly-kpi endpoint's ``movement`` block (same
                Snowflake five-bucket query as the Pipeline Movement tab).
                Each row shows count + $ — CLTND/CLTC track HubSpot's
                closed_lost text (starts-with "Lost to No Decision" or
                "Lost to Competitor"). */}
            <KpiBlock title="Pipeline movement (this week)">
              <KpiMovementRow
                label="CLTND"
                hint="Closed Lost / No Decision (HubSpot closed_lost = Lost to No Decision*)"
                cents={kpi.data?.movement?.cltnd_cents ?? null}
                count={kpi.data?.movement?.cltnd_count ?? null}
              />
              <KpiMovementRow
                label="CLTC"
                hint="Closed Lost / To Competitor (HubSpot closed_lost = Lost to Competitor*)"
                cents={kpi.data?.movement?.cltc_cents ?? null}
                count={kpi.data?.movement?.cltc_count ?? null}
              />
              <KpiMovementRow
                label="Pushed"
                hint="Open deals whose close date sits in the current month but whose stage hasn't moved since before this week — Stan's signal a deal slipped."
                cents={kpi.data?.movement?.pushed_cents ?? null}
                count={kpi.data?.movement?.pushed_count ?? null}
              />
              <KpiMovementRow
                label="New pipeline added"
                hint="Sum of deal.amount across deals entering Sales Qualified this week."
                cents={kpi.data?.movement?.new_pipeline_cents ?? null}
                count={kpi.data?.movement?.new_pipeline_count ?? null}
              />
              <KpiMovementRow
                label="Advanced pipeline"
                hint="Sum of deal.amount that moved into Technical Validation or Contract Negotiation this week."
                cents={kpi.data?.movement?.advanced_cents ?? null}
                count={kpi.data?.movement?.advanced_count ?? null}
              />
            </KpiBlock>

            {/* Block 5 — CRO net-revenue forecast */}
            <KpiBlock title="CRO forecast (net revenue)">
              <KpiRow
                label={`${fmtMonthName(data.fiscal_month)} forecast`}
                value={
                  sumForecast(data.forecast_by_segment_month) -
                  data.churn_forecast_month_cents
                }
              />
              <KpiRow
                label={`Q${data.quarter} forecast`}
                value={
                  sumForecast(data.forecast_by_segment_quarter) -
                  data.churn_forecast_quarter_cents
                }
              />
            </KpiBlock>

            {/* AE pacing (kept from Phase 3 — still useful here) */}
            {kpi.data && kpi.data.per_ae.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                    AE pipeline pacing — Q{kpi.data.quarter} {kpi.data.fiscal_year}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>AE</TableHead>
                        <TableHead className="text-right">Weekly pace</TableHead>
                        <TableHead className="text-right">Cumulative added</TableHead>
                        <TableHead className="text-right">% to pace</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {kpi.data.per_ae.map((ae) => (
                        <AePacingRowView key={ae.rep_id} ae={ae} />
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Weekly KPI history table — unchanged */}
            {kpi.data && kpi.data.rows.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                    Weekly trend — Q{kpi.data.quarter} {kpi.data.fiscal_year}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Week starting</TableHead>
                        <TableHead className="text-right">New companies</TableHead>
                        <TableHead className="text-right">SQOs</TableHead>
                        <TableHead className="text-right">Pipeline added</TableHead>
                        <TableHead className="text-right">Closed-won</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {kpi.data.rows.map((r) => (
                        <TableRow key={r.week_start}>
                          <TableCell className="font-mono text-xs">
                            {fmtDate(r.week_start)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {fmtCount(r.new_companies)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {fmtCount(r.sqo_count)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {fmtCents(r.pipeline_added_cents)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-success">
                            {fmtCents(r.closed_won_cents)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function KpiBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableBody>{children}</TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function KpiRow({
  label,
  value,
  placeholder,
}: {
  label: string;
  value: number | null;
  placeholder?: string;
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">{label}</TableCell>
      <TableCell className="text-right font-mono">
        {value == null ? (
          placeholder ? (
            <InfoPopover title={label}>{placeholder}</InfoPopover>
          ) : (
            <span className="text-muted-foreground italic">—</span>
          )
        ) : (
          fmtCents(value)
        )}
      </TableCell>
      {placeholder && value == null && (
        <TableCell className="w-8">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-warning">
            <HelpCircle className="h-3 w-3" /> Confirm
          </span>
        </TableCell>
      )}
    </TableRow>
  );
}

function KpiMovementRow({
  label,
  hint,
  cents,
  count,
}: {
  label: string;
  hint?: string;
  cents: number | null;
  count: number | null;
}) {
  const hasData = cents !== null && count !== null;
  return (
    <TableRow>
      <TableCell className="font-medium">
        <span className="inline-flex items-center gap-1">
          {label}
          {hint && (
            <InfoPopover title={label} className="inline-flex">
              {hint}
            </InfoPopover>
          )}
        </span>
      </TableCell>
      <TableCell className="text-right font-mono">
        {hasData ? (
          <span className="inline-flex items-baseline gap-2">
            <span>{fmtCents(cents)}</span>
            <span className="text-[10px] text-muted-foreground">
              ({fmtCount(count!)} {count === 1 ? "deal" : "deals"})
            </span>
          </span>
        ) : (
          <span className="italic text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

function KpiCountRow({ label, value }: { label: string; value: number }) {
  return (
    <TableRow>
      <TableCell className="font-medium">{label}</TableCell>
      <TableCell className="text-right font-mono">{fmtCount(value)}</TableCell>
    </TableRow>
  );
}

function KpiPctRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | null;
  hint?: string;
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        <span className="inline-flex items-center gap-1">
          {label}
          {hint && (
            <InfoPopover title={label} className="inline-flex">
              {hint}
            </InfoPopover>
          )}
        </span>
      </TableCell>
      <TableCell className="text-right font-mono">{fmtPct(value)}</TableCell>
    </TableRow>
  );
}

function AePacingRowView({ ae }: { ae: AePacingRow }) {
  const lastWeek = ae.weeks[ae.weeks.length - 1];
  const pctToPace = lastWeek?.pct_to_pace ?? null;
  const onTrack = pctToPace != null && pctToPace >= 1.0;
  return (
    <TableRow>
      <TableCell className="font-medium">
        <span className="flex flex-col">
          <span>{ae.display_name}</span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {ae.tier}
          </span>
        </span>
      </TableCell>
      <TableCell className="text-right font-mono">
        {fmtCents(ae.weekly_pace_target_cents)}
      </TableCell>
      <TableCell className="text-right font-mono">
        {fmtCents(ae.cumulative_cents)}
      </TableCell>
      <TableCell
        className={`text-right font-mono ${
          onTrack ? "text-success" : pctToPace != null && pctToPace < 0.7 ? "text-danger" : ""
        }`}
      >
        {pctToPace != null ? fmtPct(pctToPace * 100, 0) : "—"}
      </TableCell>
    </TableRow>
  );
}

// ── helpers ────────────────────────────────────────────────────────────

function sumClosed(rows: { closed_cents: number }[]): number {
  return rows.reduce((acc, r) => acc + (r.closed_cents ?? 0), 0);
}

function sumForecast(rows: { forecast_cents?: number | null }[]): number {
  return rows.reduce((acc, r) => acc + (r.forecast_cents ?? 0), 0);
}

function coveragePct(openCents: number, targetCents: number): number | null {
  if (!targetCents) return null;
  return (openCents / targetCents) * 100;
}
